import type { ReactNode } from 'react';
import { Stack, Text, ThemeIcon } from '@mantine/core';
import type { Icon } from '@tabler/icons-react';

/**
 * Friendly "nothing here" placeholder (2026-08-24 affordance audit: empty states
 * across the app were bare grey one-liners; this matches the Unleashed reference
 * style instead — muted icon, short title, optional hint/action). Purely
 * presentational: callers decide when there's nothing to show and pass the copy;
 * this component holds no data-fetching or empty-check logic of its own.
 *
 * Design-uplift pass (2026-08-25): a dashed "empty slot" boundary — reads as an
 * intentional placeholder rather than a stray icon floating on the page — plus a
 * bigger, bolder icon chip and title so the action (when passed) has a clear
 * "here's what fills this in" landing spot beneath it. Border uses Mantine's own
 * `--mantine-color-default-border` variable (verified against the installed
 * package's stylesheet) rather than a hardcoded gray so it's already
 * scheme-aware — no separate light/dark branch needed.
 */
export function EmptyState({
  icon: TabIcon,
  title,
  hint,
  action,
}: {
  icon: Icon;
  title: string;
  hint?: string;
  action?: ReactNode;
}): JSX.Element {
  return (
    <Stack
      align="center"
      gap="xs"
      py="xl"
      px="md"
      style={{ border: '1px dashed var(--mantine-color-default-border)', borderRadius: 'var(--mantine-radius-lg)' }}
    >
      <ThemeIcon size={64} radius="xl" variant="light" color="graphite">
        <TabIcon size={30} stroke={1.5} />
      </ThemeIcon>
      <Text fw={600} size="md">
        {title}
      </Text>
      {hint && (
        <Text size="sm" c="dimmed" ta="center" maw={360}>
          {hint}
        </Text>
      )}
      {action}
    </Stack>
  );
}
